'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { cn } from '@shared/utils/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProductsFilters } from '../../hooks/useProductsFilters';

interface ProductsFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  hasTeamData?: boolean;
  className?: string;
}

export const ProductsFiltersPanel = ({
  initialConditions = [],
  onChange,
  hasTeamData = false,
  className,
}: ProductsFiltersPanelProps) => {
  const { t } = useTranslation('productsModule');

  const {
    filters,
    handleCodeChange,
    handleCreatedAtChange,
    handleClientChange,
    handleCategoryChange,
    handleCostChange,
    handleAvailableChange,
    handleReservedChange,
    handleStockChange,
    clearAllFilters,
    clientOptions,
    isLoadingClients,
    categoryOptions,
    isLoadingCategories,
  } = useProductsFilters({
    initialConditions,
    onChange,
    hasTeamData,
  });

  // Estado local para inputs de texto/número para aplicar en blur/enter
  const [localState, setLocalState] = useState({
    code: filters.code,
    cost: { ...filters.cost },
    available: { ...filters.available },
    reserved: { ...filters.reserved },
    stock: { ...filters.stock },
  });

  // Sincronizar estado local
  useEffect(() => {
    setLocalState({
      code: filters.code,
      cost: { ...filters.cost },
      available: { ...filters.available },
      reserved: { ...filters.reserved },
      stock: { ...filters.stock },
    });
  }, [filters]);

  const applyCodeFilter = () => {
    if (localState.code !== filters.code) {
      handleCodeChange(localState.code);
    }
  };

  const applyRangeFilter = (
    field: 'cost' | 'available' | 'reserved' | 'stock',
    handler: (type: 'min' | 'max', value: string) => void,
  ) => {
    if (localState[field].min !== filters[field].min) {
      handler('min', localState[field].min);
    }
    if (localState[field].max !== filters[field].max) {
      handler('max', localState[field].max);
    }
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.code) count++;
    if (filters.createdAt?.from && filters.createdAt?.to) count++;
    if (filters.client.length > 0) count++;
    if (filters.category.length > 0) count++;
    if (filters.cost.min || filters.cost.max) count++;
    if (filters.available.min || filters.available.max) count++;
    if (filters.reserved.min || filters.reserved.max) count++;
    if (filters.stock.min || filters.stock.max) count++;
    return count;
  }, [filters]);

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') action();
  };

  return (
    <div className={cn('space-y-5', className)}>
      {/* SECCIÓN: DATOS BÁSICOS */}
      <FilterSection title={t('filters.basic_info', 'Información básica')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.code', 'Código')}>
            <input
              type="text"
              value={localState.code}
              onChange={(e) => setLocalState({ ...localState, code: e.target.value })}
              onBlur={applyCodeFilter}
              onKeyDown={(e) => handleKeyDown(e, applyCodeFilter)}
              placeholder={t('search.placeholder', 'Buscar...')}
              className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              data-testid="filter-code"
            />
          </FilterField>

          <FilterField label={t('filters.created_at', 'Creado en')}>
            <DateRangePicker
              initialDateFrom={filters.createdAt?.from}
              initialDateTo={filters.createdAt?.to}
              onUpdate={({ range }) => handleCreatedAtChange(range)}
              placeholder="Seleccionar..."
              dataTestId="filter-created-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>

          {hasTeamData && (
            <FilterField label={t('filters.client', 'Cliente')}>
              <MultiSelect
                options={clientOptions}
                value={filters.client}
                onValueChange={handleClientChange}
                placeholder={isLoadingClients ? 'Cargando...' : 'Todos los clientes'}
                emptyText={t('filters.notFound', 'Sin resultados')}
                disabled={isLoadingClients}
                dataTestId="filter-client"
                triggerClassName="h-[38px]"
              />
            </FilterField>
          )}

          <FilterField label={t('filters.category', 'Categoría')}>
            <MultiSelect
              options={categoryOptions}
              value={filters.category}
              onValueChange={handleCategoryChange}
              placeholder={
                isLoadingCategories
                  ? t('filters.loading_categories', 'Cargando...')
                  : t('filters.all_categories', 'Todas las categorías')
              }
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={isLoadingCategories}
              dataTestId="filter-category"
              triggerClassName="h-[38px]"
            />
          </FilterField>
        </div>
      </FilterSection>

      {/* SECCIÓN: COSTES Y STOCK */}
      <FilterSection title={t('filters.stock_and_cost', 'Costes y Stock')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <RangeFilterField
            label={t('filters.cost', 'Coste')}
            min={localState.cost.min}
            max={localState.cost.max}
            onMinChange={(v) => setLocalState((prev) => ({ ...prev, cost: { ...prev.cost, min: v } }))}
            onMaxChange={(v) => setLocalState((prev) => ({ ...prev, cost: { ...prev.cost, max: v } }))}
            onBlur={() => applyRangeFilter('cost', handleCostChange)}
            onEnter={() => applyRangeFilter('cost', handleCostChange)}
          />

          <RangeFilterField
            label={t('filters.available', 'Disponible')}
            min={localState.available.min}
            max={localState.available.max}
            onMinChange={(v) => setLocalState((prev) => ({ ...prev, available: { ...prev.available, min: v } }))}
            onMaxChange={(v) => setLocalState((prev) => ({ ...prev, available: { ...prev.available, max: v } }))}
            onBlur={() => applyRangeFilter('available', handleAvailableChange)}
            onEnter={() => applyRangeFilter('available', handleAvailableChange)}
          />

          <RangeFilterField
            label={t('filters.reserved', 'Reservado')}
            min={localState.reserved.min}
            max={localState.reserved.max}
            onMinChange={(v) => setLocalState((prev) => ({ ...prev, reserved: { ...prev.reserved, min: v } }))}
            onMaxChange={(v) => setLocalState((prev) => ({ ...prev, reserved: { ...prev.reserved, max: v } }))}
            onBlur={() => applyRangeFilter('reserved', handleReservedChange)}
            onEnter={() => applyRangeFilter('reserved', handleReservedChange)}
          />

          <RangeFilterField
            label={t('filters.stock', 'Stock Total')}
            min={localState.stock.min}
            max={localState.stock.max}
            onMinChange={(v) => setLocalState((prev) => ({ ...prev, stock: { ...prev.stock, min: v } }))}
            onMaxChange={(v) => setLocalState((prev) => ({ ...prev, stock: { ...prev.stock, max: v } }))}
            onBlur={() => applyRangeFilter('stock', handleStockChange)}
            onEnter={() => applyRangeFilter('stock', handleStockChange)}
          />
        </div>
      </FilterSection>

      {/* Footer con acciones */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
          >
            Limpiar {activeFiltersCount} {activeFiltersCount === 1 ? 'filtro' : 'filtros'}
          </Button>
        </div>
      )}
    </div>
  );
};

const FilterSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-3">
    <h4 className="text-sm font-semibold text-foreground">{title}</h4>
    {children}
  </div>
);

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

interface RangeFilterFieldProps {
  label: string;
  min: string;
  max: string;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
  onBlur: () => void;
  onEnter: () => void;
}

const RangeFilterField = ({ label, min, max, onMinChange, onMaxChange, onBlur, onEnter }: RangeFilterFieldProps) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onEnter();
  };

  return (
    <FilterField label={label}>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={min}
          onChange={(e) => onMinChange(e.target.value)}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          placeholder="Min"
          className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        />
        <span className="text-muted-foreground">-</span>
        <input
          type="number"
          value={max}
          onChange={(e) => onMaxChange(e.target.value)}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          placeholder="Max"
          className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        />
      </div>
    </FilterField>
  );
};
